import React, { type ReactNode } from "react";
import { Modal as BootstrapModal } from "react-bootstrap";
import Drawer from "@mui/material/Drawer";
import { isMobile } from "react-device-detect";
import { motion, AnimatePresence, type HTMLMotionProps } from "framer-motion";
import { useSwipeable } from "react-swipeable";

type DrawerHandleProps = {
    onHide: () => void;
};

/**
 * DrawerHandle component that renders a swipeable handle for the drawer
 *
 * @component
 * @param {Object} props - The component props
 * @param {Function} props.onHide - Function to handle the hide action
 * @returns {JSX.Element} The rendered handle component
 */
const DrawerHandle: React.FC<DrawerHandleProps> = ({ onHide }) => (
    <motion.div
        className="tw-flex tw-justify-center tw-pt-3 tw-pb-2"
        onClick={onHide}
        title="Close drawer"
        aria-label="Close drawer"
        style={{ cursor: "pointer" }}
        whileHover={{ scale: 1.1 }}
    >
        <div className="tw-w-9 tw-h-1 tw-bg-gray-300 tw-rounded-full" />
    </motion.div>
);

type ResponsiveWrapperProps = {
    show: boolean;
    onHide: () => void;
    children: ReactNode;
    className?: string;
    /** Optional className applied to the Bootstrap Modal content. */
    contentClassName?: string;
    /** Optional className applied to the Bootstrap Modal dialog (desktop only). */
    dialogClassName?: string;
    animate?: boolean;
    drawerHeight?: string | number;
    showHandle?: boolean;
    isExpandable?: boolean;
    isExpanded?: boolean;
    expandedHeight?: string | number;
    onExpand?: () => void;
    /** Vertically centers the desktop Bootstrap modal. */
    centered?: boolean;
} & Omit<
    HTMLMotionProps<"div">,
    keyof {
        show: boolean;
        onHide: () => void;
        children: ReactNode;
        className?: string;
        contentClassName?: string;
        dialogClassName?: string;
        animate?: boolean;
        drawerHeight?: string | number;
        showHandle?: boolean;
        isExpandable?: boolean;
        isExpanded?: boolean;
        expandedHeight?: string | number;
        onExpand?: () => void;
        centered?: boolean;
    }
>;

/**
 * ResponsiveWrapper component that conditionally renders either a modal or a drawer
 * based on the device type (mobile or desktop).
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.show - Boolean flag to control the visibility of the modal/drawer.
 * @param {Function} props.onHide - Function to handle the hide action for the modal/drawer.
 * @param {React.ReactNode} props.children - The content to be displayed within the modal/drawer.
 * @param {String} props.className - Classname to be used also in PaperProps.
 * @param {boolean} props.animate - Whether to animate the modal/drawer. Defaults to true.
 * @param {String} props.drawerHeight - Height for the mobile drawer. Defaults to '90vh' if not provided.
 * @param {boolean} props.showHandle - Whether to show a horizontal swipe handle at the top of the drawer (mobile only).
 * @param {boolean} props.isExpandable - Whether the drawer can be expanded to show more content.
 * @param {boolean} props.isExpanded - Whether the drawer is currently expanded.
 * @param {String} props.expandedHeight - Height for the expanded mobile drawer.
 * @param {Function} props.onExpand - Function to handle expansion of the drawer.
 * @returns {JSX.Element} The rendered component.
 */
const ResponsiveWrapper: React.FC<ResponsiveWrapperProps> = ({
    show,
    onHide,
    children,
    className,
    contentClassName,
    dialogClassName,
    animate = true,
    drawerHeight,
    showHandle = false,
    isExpandable = false,
    isExpanded = false,
    expandedHeight = "95vh",
    onExpand,
    centered = false,
    ...motionProps
}) => {
    const slideAnimation = animate
        ? {
              initial: { y: "100%" },
              animate: { y: 0 },
              exit: { y: "100%" },
              transition: {
                  type: "spring" as const,
                  damping: 30, // Damping to reduce bounce
                  stiffness: 200, // Stiffness for smoother motion
                  mass: 0.8, // Mass to make it feel more solid
              },
          }
        : {};

    const fadeAnimation = animate
        ? {
              initial: { opacity: 0, scale: 0.95 },
              animate: { opacity: 1, scale: 1 },
              exit: { opacity: 0, scale: 0.95 },
              transition: {
                  type: "spring" as const,
                  damping: 30,
                  stiffness: 200,
                  mass: 0.8,
              },
          }
        : {};

    // Determine the current height based on expansion state
    const currentHeight = isExpandable && isExpanded ? expandedHeight : drawerHeight || "90vh";

    // Swipe handlers for expandable functionality
    const rawSwipeHandlers = useSwipeable({
        onSwipedUp: () => {
            if (isExpandable && !isExpanded && onExpand) {
                onExpand();
            }
        },
        trackMouse: true,
    });

    // Cast swipe handlers to motion props shape for safe spreading on motion elements
    const swipeHandlers = rawSwipeHandlers as unknown as HTMLMotionProps<"div">;

    return isMobile ? (
        <Drawer
            anchor="bottom"
            open={show}
            onClose={onHide}
            PaperProps={{
                sx: {
                    height: currentHeight,
                    background: animate ? "transparent" : "white",
                    boxShadow: animate ? "none" : undefined,
                    overflow: "hidden", // Prevent any potential overflow during animation
                },
                className,
            }}
            SlideProps={{
                timeout: animate ? 300 : 0,
            }}
        >
            {animate ? (
                <AnimatePresence mode="wait">
                    {show && (
                        <motion.div
                            {...slideAnimation}
                            className="tw-bg-white tw-h-full tw-rounded-t-[16px] tw-overflow-hidden"
                            style={{ touchAction: "none" }} // Prevents touch events from interfering with animation
                            {...swipeHandlers}
                            {...(motionProps as any)}
                        >
                            {showHandle && <DrawerHandle onHide={onHide} />}
                            {children}
                        </motion.div>
                    )}
                </AnimatePresence>
            ) : (
                <div className="tw-bg-white tw-h-full tw-overflow-hidden" {...(swipeHandlers as any)}>
                    {showHandle && <DrawerHandle onHide={onHide} />}
                    {children}
                </div>
            )}
        </Drawer>
    ) : (
        <BootstrapModal
            show={show}
            onHide={onHide}
            centered={centered}
            className={className}
            contentClassName={contentClassName}
            dialogClassName={dialogClassName}
        >
            {animate ? (
                <AnimatePresence mode="wait">
                    {show && (
                        <motion.div {...fadeAnimation} {...(motionProps as any)}>
                            {children}
                        </motion.div>
                    )}
                </AnimatePresence>
            ) : (
                <div>{children}</div>
            )}
        </BootstrapModal>
    );
};

export default ResponsiveWrapper;
